/* Atlas v3 — sector comparison lines for the Overview hero.

   One line per main-tier sector, normalised to % change from the start of the
   window so they're readable against each other (and against the atlas_index
   line, which uses the same methodology — see the sector_index migration).

   COLOUR CARRIES IDENTITY. This deliberately departs from the v3 rule that
   amber is the only non-semantic accent: that rule governs UI chrome, and a
   nine-series comparison is a categorical-encoding problem, which is the one
   job only colour does well. The first cut obeyed the rule literally — nine
   amber lines separated by dash patterns — and at 390px it read as mud (Ben:
   "it sucks ... some hand rolled looking thing"). Dash-only encoding is a
   documented anti-pattern for more than about three series.

   ALL NINE LINES ARE SOLID. A first pass gave `grid` a dashed variant of
   `energy`'s red (they share a label), but on a nine-line chart the dash
   carried no information Ben could use — "the different linetypes arent doing
   anything it should be same linetype but different colors" — so it takes a
   ninth hue instead. See the SERIES map for the validated set and the exact
   validator invocation.

   Nine categorical hues cannot clear the ALL-pairs gate; no palette of this
   size can, which the method documents. Identity is therefore never carried by
   colour alone: the legend swatches every series, and clicking EITHER a legend
   row or the line itself names the sector on the chart.

   Colour follows the ENTITY, not its rank: the map is keyed by sector_id, so
   isolating or excluding a sector never repaints the survivors. */
import { useEffect, useMemo, useRef, useState } from 'react'
import { createChart, LineSeries, LineStyle, CrosshairMode, PriceScaleMode } from 'lightweight-charts'

// Sectors Ben excluded from the comparison (not part of the AI/energy thesis
// this chart is for). Excluded HERE rather than in the cron: the index is
// generic data, this is a presentation choice.
// Keyed on sector_id, not label: ids are stable identifiers, labels are
// editorial text that changes and is not even unique (two sectors are both
// labelled "Energy & Grid"). Matching on labels also meant that any hiccup in
// label resolution silently un-excluded both sectors.
const EXCLUDED = new Set(['ai_health', 'defense'])

// sector_id → hex. Fixed assignment, never cycled or index-derived.
// ALL SOLID. `grid` used to share `energy`'s red as a dashed line (they carry
// the same label), but on a nine-line chart the dash was doing no work — Ben:
// "the different linetypes arent doing anything it should be same linetype but
// different colors". So it takes a ninth hue instead, validated as a set in
// LEGEND order (the order a reader actually compares) against true black:
//   validate_palette.js "#3987e5,#e66767,#00a5c8,#d95926,#9085e9,#199e70,#c98500,#d55181,#008300" --mode dark --surface "#000000"
//   lightness PASS · chroma PASS · CVD ΔE 8.4 PASS · normal-vision 19.3 PASS · contrast PASS
// Nine categorical hues cannot clear the ALL-pairs gate — no palette of this
// size can, which the method documents — so identity is never colour-alone:
// the legend swatches it, and tapping either the legend or the line itself
// names the sector.
const SERIES = {
  cloud:         '#3987e5',  // blue
  energy:        '#e66767',  // red
  grid:          '#00a5c8',  // cyan
  memory:        '#d95926',  // orange
  ai_networking: '#9085e9',  // violet
  photonics:     '#199e70',  // aqua
  power:         '#c98500',  // yellow
  quantum:       '#d55181',  // magenta
  upstream:      '#008300',  // green
}
const UNMAPPED = '#8a8880'

// Short legend labels. The API returns editorial names ("Semiconductor
// Equipment & Materials"), and nine of those wrapped the legend onto four rows
// on a phone — more vertical space than the chart itself. The full name still
// appears in the click callout, so nothing is lost.
// It also retires the ugliest artefact of the duplicate label: `energy` and
// `grid` both read "Energy & Grid" upstream, which forced "(energy)"/"(grid)"
// suffixes. Here they are simply Energy and Grid.
const SHORT = {
  cloud: 'Cloud',
  energy: 'Energy',
  grid: 'Grid',
  memory: 'Memory',
  ai_networking: 'Networking',
  photonics: 'Photonics',
  power: 'Power',
  quantum: 'Quantum',
  upstream: 'Semi Equip',
}

// #rrggbb + alpha → rgba(). Used for the dimmed state so isolation keeps hue.
function hexA(hex, a) {
  const n = parseInt(hex.slice(1), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
}

function tk(el, name, alpha) {
  const v = getComputedStyle(el).getPropertyValue(name).trim()
  if (!v) return alpha != null ? 'rgba(0,0,0,0)' : '#888'
  return alpha != null ? `hsl(${v} / ${alpha})` : `hsl(${v})`
}

// `log` plots growth-of-100 on a logarithmic axis instead of % on a linear one.
// Over 5Y/MAX the spread between sectors runs to two orders of magnitude
// (quantum past +15,000% against upstream's ~+680%), and on a linear axis the
// largest line squashes every other one onto the baseline — the comparison the
// chart exists for stops being readable. Percent cannot be logged because it
// goes negative, so the log view uses `growth` (always positive) and converts
// back to % for every label the reader sees.
// Toggle one sector in a selection array. Exported because both Overview
// surfaces own the persisted selection (so each can title its own headline
// from it) and must mutate it identically.
// APPEND on add — never sort. The palette is keyed by sector_id so the colours
// are stable regardless, but selection ORDER is what the headline reads, and a
// sort would reorder "Memory vs Semi Equip" under the reader mid-session.
export function toggleSector(selected, id) {
  const cur = Array.isArray(selected) ? selected : []
  return cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
}

// The hero headline for the sectors view. Lives here because it needs both
// EXCLUDED and SHORT, and because the mobile and desktop Overviews must title
// the same chart the same way.
//
// A selection is filtered against what the API actually returned before being
// counted: a persisted pick naming a sector that has since been dropped from
// the universe must not be headlined as though its line were on screen.
export function sectorHeadline(series, selected) {
  const eligible = (series || []).filter((x) => !EXCLUDED.has(x.sector_id))
  const live = (selected || []).filter((id) => eligible.some((e) => e.sector_id === id))
  if (live.length === 0) return `${eligible.length} sectors`
  // Naming them only reads at two or three; past that the legend is the
  // faster read and a count is the honest summary.
  if (live.length <= 3) return live.map((id) => SHORT[id] || id).join(' vs ')
  return `${live.length} sectors`
}

export function SectorLines({ series, height = 168, days = 365, log = false,
                              selected = [], onToggle, onReset }) {
  const wrapRef = useRef(null)
  const [hover, setHover] = useState(null)   // sector_id NAMED by a chart click
  const [vals, setVals] = useState(null)     // { [sector_id]: pct } at crosshair
  const handlesRef = useRef(new Map())       // sector_id → series, for isolation

  // Every sector eligible for the comparison — the legend's row set. It stays
  // the FULL list even when a subset is drawn, because the legend is the only
  // way back: a legend that listed just the drawn lines would make deselecting
  // a sector the last thing you could ever do to it.
  const selectable = useMemo(
    () => (series || []).filter((x) => !EXCLUDED.has(x.sector_id)),
    [series],
  )

  // The lines actually drawn. An empty selection means ALL of them, which is
  // both the original behaviour and the right default — the chart is a survey
  // until you narrow it.
  //
  // Memoised: this sits in the chart effect's deps, and a fresh array every
  // render would tear the chart down and rebuild it on every parent render —
  // Overview re-renders on each live-price poll, so that is constant churn.
  const selKey = (selected || []).join(',')
  const shown = useMemo(
    () => (selKey ? selectable.filter((x) => selKey.split(',').includes(x.sector_id)) : selectable),
    [selectable, selKey],
  )

  useEffect(() => {
    const el = wrapRef.current
    if (!el || shown.length === 0) return

    const chart = createChart(el, {
      autoSize: true,
      layout: {
        background: { type: 'solid', color: 'transparent' },
        textColor: tk(el, '--studio-text-subtle'),
        fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, attributionLogo: false,
      },
      grid: { vertLines: { visible: false }, horzLines: { color: tk(el, '--studio-text', 0.05) } },
      leftPriceScale: { visible: false, borderVisible: false },
      rightPriceScale: {
        visible: true, borderVisible: false, scaleMargins: { top: 0.12, bottom: 0.12 },
        minimumWidth: 46,
        mode: log ? PriceScaleMode.Logarithmic : PriceScaleMode.Normal,
      },
      timeScale: { visible: true, borderVisible: false, fixLeftEdge: true, fixRightEdge: true },
      // In log mode the axis values are growth-of-100, so convert back to a
      // percentage for display — the reader should never see "100" for flat.
      localization: {
        // Compact past 1,000%: "+3900.0%" is wider than the price scale and got
        // clipped to "+3?00.0" on a 390px screen. k-notation fits.
        priceFormatter: (v) => {
          const pct = log ? v - 100 : v
          const sign = pct >= 0 ? '+' : '-'
          const a = Math.abs(pct)
          if (a >= 1000) return `${sign}${(a / 1000).toFixed(a >= 10000 ? 0 : 1)}k%`
          return `${sign}${a.toFixed(a >= 100 ? 0 : 1)}%`
        },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: tk(el, '--studio-border', 0.9), width: 1, style: LineStyle.Dashed, labelVisible: true, labelBackgroundColor: tk(el, '--studio-accent') },
        horzLine: { visible: false, labelVisible: false },
      },
      handleScroll: false, handleScale: false,
    })

    const handles = new Map()
    shown.forEach((s) => {
      const hex = SERIES[s.sector_id] || UNMAPPED
      const line = chart.addSeries(LineSeries, {
        color: hex,
        lineWidth: 2,
        lineStyle: LineStyle.Solid,
        priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: true,
      })
      line.setData(s.points.map((p) => ({
        time: Math.floor(new Date(p.date + 'T00:00:00Z').getTime() / 1000),
        // Older payloads have no `growth`; fall back so a cached response
        // can't render an empty chart.
        value: log ? (p.growth != null ? p.growth : p.pct + 100) : p.pct,
      })))
      handles.set(s.sector_id, line)
    })

    // Zero reference. Everything is normalised to the window start, so "above
    // or below the line you started at" is the whole reading — but there was
    // no baseline drawn, only unlabelled gridlines.
    const first = handles.values().next().value
    if (first) {
      first.createPriceLine({
        price: log ? 100 : 0, color: tk(el, '--studio-text-subtle', 0.5),
        lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: false,
      })
    }

    const onCross = (param) => {
      if (!param || param.time == null || !param.seriesData) { setVals(null); return }
      const out = {}
      for (const [id, h] of handles) {
        const d = param.seriesData.get(h)
        if (d && d.value != null) out[id] = d.value
      }
      setVals(out)
    }
    chart.subscribeCrosshairMove(onCross)

    // Click a LINE to name it — the legend was the only way to identify a
    // series, which means reading the chart required looking away from it.
    // Picks the series whose value at the clicked time is nearest the click in
    // PIXELS (not in value space: on a log axis equal value gaps are wildly
    // unequal distances). Clicking away from every line clears the isolation.
    const onClick = (param) => {
      if (!param || param.point == null) return          // outside the chart
      // Inside the plot but off the data (the margins above/below every line)
      // reports no time — that is a "clear", not a no-op. Returning early here
      // meant a click on empty space left the previous selection stuck on.
      if (param.time == null || !param.seriesData) { setHover(null); return }
      let best = null, bestDy = Infinity
      for (const [id, h] of handles) {
        const d = param.seriesData.get(h)
        if (!d || d.value == null) continue
        const y = h.priceToCoordinate(d.value)
        if (y == null) continue
        const dy = Math.abs(y - param.point.y)
        if (dy < bestDy) { bestDy = dy; best = id }
      }
      // Near a line SELECTS it; away from every line clears. Deliberately not a
      // toggle on re-click: the handler can fire more than once for a single
      // physical click, and a toggle is not idempotent under that — it took two
      // taps to clear and read as the chart ignoring the first. Selection is
      // idempotent, so a duplicate fire is harmless. Clearing is the callout's
      // × or the active legend row.
      // ~22px: forgiving for a fingertip, tight enough that a tap in open space
      // still reads as "clear".
      setHover(bestDy <= 22 ? best : null)
    }
    chart.subscribeClick(onClick)

    handlesRef.current = handles
    chart.timeScale().fitContent()
    return () => {
      chart.unsubscribeCrosshairMove(onCross)
      chart.unsubscribeClick(onClick)
      handlesRef.current = new Map()
      chart.remove()
    }
  }, [series, height, days, shown, log])

  // Isolation applied by applyOptions rather than by rebuilding the chart.
  // `hover` used to sit in the build effect's deps, so every click tore the
  // chart down and recreated it — a visible flash, and worse now that clicking
  // the chart itself sets it.
  useEffect(() => {
    // Deselecting the sector a chart click had NAMED leaves `hover` pointing at
    // a line that is no longer drawn — every remaining series would then read
    // as "not the isolated one" and the whole chart would dim to 14% alpha.
    // Treat a name with no live handle as no name at all.
    const named = hover != null && handlesRef.current.has(hover) ? hover : null
    for (const [id, h] of handlesRef.current) {
      const hex = SERIES[id] || UNMAPPED
      const dimmed = named != null && named !== id
      // Dimming keeps the HUE and drops the alpha, so an isolated view still
      // ties each line to its legend swatch.
      h.applyOptions({
        color: dimmed ? hexA(hex, 0.14) : hex,
        lineWidth: named === id ? 3 : 2,
        crosshairMarkerVisible: !dimmed,
      })
    }
  }, [hover, shown])

  // Not reachable through the legend (removing the last sector is blocked
  // below), but a stale persisted selection naming only sectors the API no
  // longer returns lands here — say which of the two it is.
  if (shown.length === 0 && selectable.length > 0) {
    return (
      <div>
        <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--studio-font-mono)', fontSize: 10, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))' }}>
          NO SECTORS SELECTED
        </div>
        {onReset && (
          <button onClick={onReset} style={{ all: 'unset', cursor: 'pointer', display: 'block', margin: '8px auto 0',
            fontFamily: 'var(--studio-font-mono)', fontSize: 9, letterSpacing: '0.12em', color: 'hsl(var(--studio-accent))' }}>
            SHOW ALL
          </button>
        )}
      </div>
    )
  }

  if (shown.length === 0) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--studio-font-mono)', fontSize: 10, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))' }}>
        NO SECTOR DATA
      </div>
    )
  }

  const picked = hover ? shown.find((x) => x.sector_id === hover) : null
  const pickedPct = picked
    ? (vals && vals[hover] != null
        ? (log ? vals[hover] - 100 : vals[hover])
        : (() => { const p = picked.points[picked.points.length - 1]; return p ? p.pct : null })())
    : null

  return (
    <div>
      {/* The chart element is owned by lightweight-charts, so the callout sits
          in a wrapper OVER it rather than inside it. Clicking a line names the
          sector right there — previously identity lived only in the legend, so
          reading the chart meant looking away from it. */}
      <div style={{ position: 'relative', width: '100%', height }}>
        <div ref={wrapRef} style={{ width: '100%', height }} />
        {picked && (
          // Clickable: clicking the same line again clears it, but the chart
          // does not reliably report a click in the empty margin above/below
          // every series, so the callout carries its own dismiss rather than
          // leaving "tap somewhere blank" as the only way out.
          <div role="button" tabIndex={0} aria-label={`Clear ${picked.name} selection`}
            onClick={() => setHover(null)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setHover(null) } }}
            style={{ position: 'absolute', top: 0, left: 0, display: 'inline-flex', alignItems: 'center', gap: 6,
            // Above the chart canvas, which otherwise swallows the dismiss click.
            zIndex: 3,
            padding: '3px 8px', borderRadius: 999, cursor: 'pointer',
            background: 'hsl(var(--studio-surface) / 0.92)', border: `1px solid ${hexA(SERIES[picked.sector_id] || UNMAPPED, 0.55)}`,
            fontFamily: 'var(--studio-font-mono)', fontSize: 9.5, letterSpacing: '0.04em', color: 'hsl(var(--studio-text))' }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: SERIES[picked.sector_id] || UNMAPPED }} />
            {picked.name}
            {pickedPct != null && (
              <span style={{ color: `hsl(var(--studio-${pickedPct >= 0 ? 'success' : 'danger'}))` }}>
                {pickedPct >= 0 ? '+' : ''}{Math.abs(pickedPct) >= 1000 ? `${(pickedPct / 1000).toFixed(1)}k` : pickedPct.toFixed(1)}%
              </span>
            )}
            <span aria-hidden="true" style={{ opacity: 0.5, marginLeft: 1 }}>×</span>
          </div>
        )}
      </div>
      {/* Legend is the SELECTION control: tap a sector to add or remove its line.
          It used to be the isolation control (tap to fade the other eight),
          which answered a different question — isolation still lives on the
          chart itself, where tapping a line names it. Filtering is the stronger
          tool anyway: dropping a sector rescales the y-axis to the survivors,
          so comparing two mid-pack sectors no longer means reading them squashed
          against whatever the biggest mover is doing.
          Rows stay in the full `selectable` order, not selection order, so a
          sector never jumps position as you toggle it. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '3px 12px', padding: '7px 2px 0' }}>
        {selectable.map((s) => {
          const hex = SERIES[s.sector_id] || UNMAPPED
          const picking = (selected || []).length > 0
          const on = picking ? selected.includes(s.sector_id) : true
          const raw = vals && vals[s.sector_id]
          const v = raw == null ? null : (log ? raw - 100 : raw)
          return (
            <button key={s.sector_id}
              // Removing the last selected sector would leave an empty chart,
              // which is never what the tap meant — treat it as "show all".
              onClick={() => {
                if (!onToggle) return
                if (on && picking && selected.length === 1) { onReset?.(); return }
                onToggle(s.sector_id)
              }}
              aria-pressed={picking ? on : false}
              style={{ all: 'unset', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                fontFamily: 'var(--studio-font-mono)', fontSize: 8.5, letterSpacing: '0.04em',
                color: `hsl(var(--studio-${on ? 'text' : 'text-muted'}))`,
                opacity: on ? 1 : 0.4, transition: 'opacity 140ms' }}>
              {/* The swatch carries identity; the label stays in a text token
                  rather than wearing the series colour. */}
              <svg width="14" height="6" aria-hidden="true" style={{ flexShrink: 0 }}>
                <line x1="0" y1="3" x2="14" y2="3" stroke={hex} strokeWidth={2} />
              </svg>
              {SHORT[s.sector_id] || s.name}
              {v != null && <span style={{ color: `hsl(var(--studio-${v >= 0 ? 'success' : 'danger'}))` }}>
                {v >= 0 ? '+' : ''}{v.toFixed(1)}%
              </span>}
            </button>
          )
        })}
        {(selected || []).length > 0 && onReset && (
          <button onClick={onReset}
            style={{ all: 'unset', cursor: 'pointer', padding: '1px 6px', borderRadius: 999,
              border: '1px solid hsl(var(--studio-accent) / 0.5)',
              fontFamily: 'var(--studio-font-mono)', fontSize: 8, letterSpacing: '0.1em',
              color: 'hsl(var(--studio-accent))' }}>
            ALL
          </button>
        )}
      </div>
    </div>
  )
}
